/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useCallback, useEffect, useState } from 'react';
import { FieldErrors, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { featureByName, setLoginLoading } from 'actions/app.actions';
import { enqueueSnackbar } from 'actions/notifications.actions';
import { SubscribeFormValues } from 'interfaces/subscribe.types';
import { appConfigSelector } from 'reducers/app.reducer';
import SubmittingOverlay from 'components/SubmittingOverlay';
import redirectAfterAuth from 'utils/redirect-after-auth';

import type { AxiosError } from 'axios';
import CenteredLoader from 'components/CenteredLoader';
import { ROUTES } from '../../constants';
import {
  useEmailAsyncValidator,
  useMatriculeAsyncValidator,
  useReferralCodeAsyncValidator,
} from './hooks/useAsyncFieldValidation';
import { useSubmitSubscribeForm } from './hooks/useSubmitSubscribeForm';
import { useSubscribeForm } from './hooks/useSubscribeForm';
import SubscribeFormUI from './SubscribeFormUI';

type SubscribeApiErrors = {
  errors?: Record<string, string>;
  message?: string;
};

/** Délai (ms) avant redirection post-submit, pour laisser le temps de lire le message de succès dans l'overlay */
const REDIRECT_DELAY_MS = 3000;
/** Délai (ms) avant focus sur l'élément scrollé, pour attendre la fin du smooth scroll */
const SCROLL_FOCUS_DELAY_MS = 400;
/** Délai (ms) pour garantir que le DOM est mis à jour avant de chercher l'élément à scroller */
const DOM_UPDATE_DELAY_MS = 50;

const SubscribeForm = (): JSX.Element => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const appConfig = useSelector(appConfigSelector);
  const { externalHomePageUrl, appLocale } = appConfig;

  // Charger les features nécessaires (comme dans l'ancien SignUp)
  useEffect(() => {
    dispatch(featureByName.request('post_user_register'));
    dispatch(featureByName.request('referral'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Hook orchestrateur principal
  const {
    context,
    initialValues,
    title,
    submitLabel,
    hasAccess,
    isLinkInvalid,
    isAlreadyActive,
    isLoading,
    isIdentityReadOnly,
    isCompanyNameRequired,
    isReferralEnabled,
    fieldConfigs,
    firstLoginConfiguration,
  } = useSubscribeForm();

  // React Hook Form v6
  const methods = useForm<SubscribeFormValues>({
    defaultValues: initialValues,
    mode: 'onBlur',
  });

  const { handleSubmit, reset, setError } = methods;

  // Scroll vers le premier champ en erreur lors du submit
  const scrollToFirstError = useCallback((fieldName?: string) => {
    // requestAnimationFrame + setTimeout garantit que le DOM est mis à jour après le re-render React
    requestAnimationFrame(() => {
      setTimeout(() => {
        let el: HTMLElement | null = null;

        if (fieldName) {
          // Chercher l'input/select/textarea par son attribut name
          el = document.querySelector<HTMLElement>(
            `input[name="${fieldName}"], select[name="${fieldName}"], textarea[name="${fieldName}"]`,
          );
          // Fallback : par id
          if (!el) {
            el = document.querySelector<HTMLElement>(`#${fieldName}`);
          }
        }

        // Dernier fallback : premier champ en erreur dans le form
        if (!el) {
          el = document.querySelector<HTMLElement>(
            'form .Mui-error input, form .Mui-error select, form .Mui-error textarea, form [aria-invalid="true"]',
          );
        }

        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Focus après la fin du smooth scroll
          setTimeout(() => el?.focus?.(), SCROLL_FOCUS_DELAY_MS);
        }
      }, DOM_UPDATE_DELAY_MS);
    });
  }, []);

  const onValidationError = useCallback(
    (errors: FieldErrors<SubscribeFormValues>) => {
      const firstFieldName = Object.keys(errors)[0];
      scrollToFirstError(firstFieldName);
    },
    [scrollToFirstError],
  );

  // Quand les initialValues changent (données pré-remplies chargées), reset le form
  useEffect(() => {
    reset(initialValues);
  }, [initialValues, reset]);

  // Mutation
  const submitMutation = useSubmitSubscribeForm(context);

  // État local pour garder le bouton désactivé jusqu'à la fin de la redirection
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Quand la requête a réussi, on bascule l'overlay en mode "succès" jusqu'à
  // ce que la redirection ait effectivement lieu.
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Validations asynchrones (debounced)
  const validateEmail = useEmailAsyncValidator(
    t('core.invalid_email_already_exists'),
  );
  const validateMatricule = useMatriculeAsyncValidator(
    t('core.invalid_unique_code_already_exists'),
  );
  const validateReferralCode = useReferralCodeAsyncValidator(
    t('auth.signup.referral.errorMessage'),
    t('auth.signup.referral.referralDoesNotExist'),
  );

  // Redirection si pas accès
  // Guard : ne pas rediriger si le formulaire est déjà en cours de soumission/succès,
  // car le 403 peut arriver sur GET /auth/first-login une fois le statut mis à jour.
  useEffect(() => {
    if (isLoading || isSubmitting) return;
    if (isAlreadyActive) {
      history.replace(ROUTES.ACCOUNT_ALREADY_ACTIVE);
    } else if (isLinkInvalid) {
      history.replace(ROUTES.LINK_EXPIRED);
    } else if (!hasAccess) {
      history.replace(ROUTES.LOGIN);
    }
  }, [isLoading, hasAccess, isLinkInvalid, isAlreadyActive, isSubmitting, history]);

  const customRedirectUrl = firstLoginConfiguration?.redirectUrl?.trim() || '';

  // Callback submit
  const onSubmit = useCallback(
    async (values: SubscribeFormValues) => {
      setIsSubmitting(true);
      setSuccessMessage(null);
      const submitStartedAt = Date.now();
      try {
        const result = await submitMutation.mutateAsync(values);
        // Inscription autonome → le JWT est posé en cookie httpOnly côté back,
        // le 200 sans erreur suffit à confirmer la création du compte.
        if (result.registered) {
          const customValidateText =
            firstLoginConfiguration?.validateText?.trim() || '';

          const minLoadingMs = 2000;
          const elapsed = Date.now() - submitStartedAt;
          const wait = Math.max(0, minLoadingMs - elapsed);

          setTimeout(() => {
            setSuccessMessage(customValidateText || null);

            if (customRedirectUrl) {
              setTimeout(() => {
                window.location.href = customRedirectUrl;
              }, REDIRECT_DELAY_MS);
            } else if (externalHomePageUrl) {
              setTimeout(() => {
                window.location.href = `${externalHomePageUrl}?redirect_to=/${appLocale !== 'en' ? appLocale : ''}`;
              }, REDIRECT_DELAY_MS);
            } else {
              setTimeout(() => {
                dispatch(setLoginLoading(false));
                redirectAfterAuth();
              }, REDIRECT_DELAY_MS);
            }
          }, wait);
          return;
        }

        // First-login / pré-inscription
        if (result.firstLoginResponse) {
          const customValidateText =
            firstLoginConfiguration?.validateText?.trim() || '';

          const minLoadingMs = 1000;
          const elapsed = Date.now() - submitStartedAt;
          const wait = Math.max(0, minLoadingMs - elapsed);

          setTimeout(() => {
            setSuccessMessage(customValidateText || null);

            if (customRedirectUrl) {
              setTimeout(() => {
                window.location.href = customRedirectUrl;
              }, REDIRECT_DELAY_MS);
            } else if (externalHomePageUrl) {
              setTimeout(() => {
                window.location.href = `${externalHomePageUrl}?redirect_to=/${appLocale !== 'en' ? appLocale : ''}`;
              }, REDIRECT_DELAY_MS);
            } else {
              setTimeout(() => {
                redirectAfterAuth();
              }, REDIRECT_DELAY_MS);
            }
          }, wait);
        }
      } catch (err) {
        setIsSubmitting(false);
        setSuccessMessage(null);
        const axiosError = err as AxiosError<SubscribeApiErrors>;
        const status = axiosError?.response?.status;
        const data = axiosError?.response?.data;

        // 422 — erreurs de validation
        if (status === 422 && data?.errors) {
          const fieldMap: Record<string, keyof SubscribeFormValues> = {
            civilite: 'gender',
            nom: 'lastName',
            prenom: 'firstName',
            email: 'email',
            matricule: 'clientId',
            mot_de_passe: 'password',
          };

          Object.entries(data.errors).forEach(([key, msg]) => {
            const formField = fieldMap[key] || key;
            setError(formField as keyof SubscribeFormValues, {
              type: 'server',
              message: msg as string,
            });
          });

          const firstServerField =
            fieldMap[Object.keys(data.errors)[0]] ||
            Object.keys(data.errors)[0];
          scrollToFirstError(firstServerField);
          return;
        }

        // 403 — compte déjà actif / suspendu
        if (status === 403) {
          dispatch(
            enqueueSnackbar({
              message: data?.message || t('auth.subscribe.error_forbidden'),
              options: { variant: 'error' },
            }),
          );
          history.push(ROUTES.LOGIN);
          return;
        }

        // Erreur générique
        dispatch(
          enqueueSnackbar({
            message: t('auth.signup.error'),
            options: { variant: 'error' },
          }),
        );
      }
    },
    [
      submitMutation,
      dispatch,
      history,
      t,
      externalHomePageUrl,
      appLocale,
      setError,
      scrollToFirstError,
      setIsSubmitting,
      setSuccessMessage,
      context,
    ],
  );

  if (isLoading) {
    return <CenteredLoader />;
  }

  if (!hasAccess) {
    return <React.Fragment />;
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit, onValidationError)}>
        <SubscribeFormUI
          title={title}
          submitLabel={submitLabel}
          context={context}
          isIdentityReadOnly={isIdentityReadOnly}
          isCompanyNameRequired={isCompanyNameRequired}
          isReferralEnabled={isReferralEnabled}
          isSubmitting={isSubmitting}
          serverErrors={null}
          validateEmail={validateEmail}
          validateMatricule={validateMatricule}
          validateReferralCode={validateReferralCode}
          fieldConfigs={fieldConfigs}
        />
      </form>
      {isSubmitting && (
        <SubmittingOverlay
          title={t(
            context === 'autonomous-registration'
              ? 'auth.subscribe.submitting.title_register'
              : 'auth.subscribe.submitting.title_continue',
          )}
          isSuccess={successMessage !== null}
          successMessage={successMessage || t('auth.subscribe.success')}
        />
      )}
    </FormProvider>
  );
};

export default SubscribeForm;
